import {computed, effect, inject, Injectable, signal} from '@angular/core';
import {AuthStore} from '@auth/data-access';
import {CompanySettingsStore} from '@company-settings/data-access';
import {StorageKeys} from '@core/storage';
import {UserStore} from '@data-users';
import {localStorageSignal} from '@ddtmm/angular-signal-generators';
import {TranslateService} from '@ngx-translate/core';
import {environment, isSupportedLanguage, LanguageType, SupportedLanguageType} from '@shared-types';
import {PrimeNGConfig} from 'primeng/api';
import {firstValueFrom} from 'rxjs';
import {Language} from './../types/language.structure';
// eslint-disable-next-line @nx/enforce-module-boundaries
import {UserModel} from '@core/api/data-access';
import {getPrimeNgTranslation} from '../primeng';
import {ActiveUserStore} from '@active-user-management';
import ILanguage = Language.ILanguage;

/**
 * active language priority:
 * 1. user preferences (fron the login response => user.preferences.language)
 * 2. company settings (company settings => defaultLanguage)
 * 3. app default (if navigator.language is supported by the app ['he','en','es'] then use it, otherwise use 'en')
 *
 *
 * * when user login, we need to check:
 * - if the user.preferences.language is set and it's not 'default', then we use it
 * - if the user.preferences.language is set to 'default':
 * 	- wait for the company settings to be loaded
 * 	- if the company settings are loaded and the defaultLanguage is set, then we use it
 * 	- if the company settings are loaded and the defaultLanguage is not set, then we use the app default
 * - if the user.preferences.language is not set, then we use the app default
 *
 */

export const AppLanguageSignal$ = localStorageSignal<SupportedLanguageType>(LanguageType.English, StorageKeys.GlobalKeys.LANGUAGE);

@Injectable({
	providedIn: 'root',
})
export class LanguageService {
	/* ------------ Providers / Services ------------ */
	private readonly translateService: TranslateService = inject(TranslateService);
	private readonly authStore: AuthStore = inject(AuthStore);
	private readonly userStore: UserStore = inject(UserStore);
	private readonly companySettingsStore = inject(CompanySettingsStore);
	private readonly primeNGConfig: PrimeNGConfig = inject(PrimeNGConfig);
	private readonly activeUserStore: ActiveUserStore = inject(ActiveUserStore);
	/* ---------------- Properties ---------------- */

	/* ---------------- Signals ---------------- */
	public readonly currentLang$ = computed<SupportedLanguageType>(() => AppLanguageSignal$());
	public readonly currentLangData = computed<ILanguage>(() => Language.Languages[this.currentLang$()]);
	public readonly currentUpdateLang$ = signal<LanguageType | null>(null);

	/* ---------------- Constructor ---------------- */
	constructor() {
		this.init();
	}

	public init(): void {
		/// OLD
		// const isLoggedIn$ = this.authStore.isLoggedInObs$;
		// const activeUser$ = this.activeUserStore.activeUserObs$;
		// const companySettings$ = this.companySettingsStore.companySettingsObs$;
		// combineLatest([isLoggedIn$, activeUser$, companySettings$])
		// 	.pipe(
		// 		map(([isLoggedIn, activeUser, companySettings]) => {
		// 			if (isLoggedIn) {
		// 				if (isLoggedIn && LanguageService.validateLanguage(activeUser.preferences.language)) return activeUser.preferences.language;
		// 				if (LanguageService.validateLanguage(companySettings.defaultLanguage)) return companySettings.defaultLanguage;
		// 				return this.getDefaultLanguage();
		// 			}
		// 			return this.getDefaultLanguage();
		// 		}),
		// 	)
		// 	.subscribe(language => {
		// 		if (isSupportedLanguage(language)) this.setAppLanguage(language);
		// 	});

		// Implementation using signals
		effect(() => {
			const isLoggedIn = this.authStore.isLoggedIn();
			const inLoginProcess = this.authStore.inLoginProcess();

			// If in login process, wait until it's finished
			if (inLoginProcess) return;

			// If not logged in, use default language
			if (!isLoggedIn) {
				const defaultLanguage = this.getDefaultLanguage();
				if (isSupportedLanguage(defaultLanguage)) {
					this.setAppLanguage(defaultLanguage);
				}
				return;
			}

			// User is logged in, determine language based on priority:
			const activeUser = this.activeUserStore.user$();

			// 1. Check user preferences
			if (activeUser && activeUser.preferences.language) {
				// If user has specific language preference (not 'default')
				if (activeUser.preferences.language !== 'default') {
					const userLanguage = LanguageService.validateLanguage(activeUser.preferences.language);
					if (userLanguage) {
						this.setAppLanguage(userLanguage);
						return;
					}
				}

				// 2. If user preference is 'default' or invalid, check company settings
				const companySettings = this.companySettingsStore.companySettings$();
				if (companySettings && companySettings.defaultLanguage) {
					const companyLanguage = LanguageService.validateLanguage(companySettings.defaultLanguage);
					if (companyLanguage) {
						this.setAppLanguage(companyLanguage);
						return;
					}
				}
			}

			// 3. Fallback to app default if user preferences and company settings don't provide a valid language
			const defaultLanguage = this.getDefaultLanguage();
			if (isSupportedLanguage(defaultLanguage)) {
				this.setAppLanguage(defaultLanguage);
			}
		});
	}

	public async setAppLanguage(language: LanguageType, updateUserPreferences = false) {
		if (this.currentUpdateLang$() === language) return; // prevent multiple calls with the same language
		this.currentUpdateLang$.set(language);
		// check if the app supports the language
		if (isSupportedLanguage(language)) {
			const needReload = await this.ensureAllLanguageDataSets(language);
			if (needReload) {
				if (updateUserPreferences) await this.updateActiveUserLanguage(language);
				this.currentUpdateLang$.set(null);
				window.location.reload();
			}
		} else {
			console.error(`Language ${language} is not supported by the app`);
		}
	}

	private async ensureAllLanguageDataSets(language: SupportedLanguageType): Promise<boolean> {
		// check if all the language data is loaded
		// environment.direction, document properties, global css variables, primeng translations, translate service:
		const languageData: ILanguage = Language.Languages[language];
		let needReload = false;
		if (languageData) {
			// update the environment direction
			if (environment.direction !== languageData.dir) {
				environment.direction = languageData.dir;
			}
			// update the document properties:
			if (document.documentElement.getAttribute('lang') !== languageData.locale_code) {
				document.documentElement.setAttribute('lang', languageData.locale_code);
			}
			if (document.documentElement.getAttribute('dir') !== languageData.dir) {
				document.documentElement.setAttribute('dir', languageData.dir);
			}
			// update the global css variables:
			if (document.documentElement.style.getPropertyValue('--dc-dir') !== languageData.dir) {
				document.documentElement.style.setProperty('--dc-dir', languageData.dir);
				document.documentElement.style.setProperty('--dc-opposite-dir', languageData.oppositeLang.dir);
			}

			if (this.primengConfigLanguage$() !== language) {
				await this.updatePrimeNGConfig(language);
			}

			const translationLanguage = this.translateService.currentLang;
			if (translationLanguage !== language) {
				await firstValueFrom(this.translateService.use(language));
			}

			// update the app language signal
			if (this.currentLang$() !== language) {
				AppLanguageSignal$.set(language);
				needReload = true;
			}
		}
		return needReload;
	}

	private async updateActiveUserLanguage(language: LanguageType): Promise<UserModel | null> {
		const activeUser = this.activeUserStore.user$() || null;
		if (activeUser) {
			activeUser.preferences.language = language;
			const updatedUser = await firstValueFrom(this.userStore.updateUser(activeUser, undefined, true));
			if (updatedUser) {
				this.activeUserStore.updateActiveUserState(updatedUser, false);
			}
			return updatedUser;
		}
		return null;
	}

	private getDefaultLanguage(): SupportedLanguageType {
		const localLanguage = AppLanguageSignal$();
		if (localLanguage) return localLanguage;
		const navigatorLanguage = navigator.language;
		return LanguageService.normalizeLanguage(navigatorLanguage) ?? LanguageType.English;
	}

	private readonly primengConfigLanguage$ = signal<SupportedLanguageType>(null);

	private updatePrimeNGConfig(language: SupportedLanguageType): Promise<boolean> {
		if (this.primengConfigLanguage$() === language) return Promise.resolve(true);
		return getPrimeNgTranslation(language)
			.catch(error => {
				console.error(`Error loading primeng translations for language ${language}:`, error);
				return false;
			})
			.then(translation => {
				this.primeNGConfig.setTranslation(translation);
				this.primengConfigLanguage$.set(language);
				return true;
			});
	}

	public static validateLanguage(language: string): SupportedLanguageType | null {
		if (isSupportedLanguage(language)) {
			return language as SupportedLanguageType;
		}
		return null;
	}

	public static normalizeLanguage(language: string): SupportedLanguageType | null {
		if (isSupportedLanguage(language)) {
			return language as SupportedLanguageType;
		}
		// example: 'en-US' => 'en'| 'he-IL' => 'he' | 'es-ES' => 'es'
		const normalizedLanguage = language.split('-')?.at(0)?.toLowerCase();
		if (isSupportedLanguage(normalizedLanguage)) {
			return normalizedLanguage as SupportedLanguageType;
		}
		return null;
	}
}
